class AdvSlideshowComponent extends HTMLElement {
  constructor() {
    super();
    
    this.currentSlide = 0;
    this.slides = this.querySelectorAll('.adv-slideshow__slide');
    this.totalSlides = this.slides.length;
    this.navButtons = {
      prev: this.querySelector('.adv-slideshow__nav-btn--prev'),
      next: this.querySelector('.adv-slideshow__nav-btn--next')
    };
    this.paginationItems = this.querySelectorAll('.adv-slideshow__pagination-item');
    
    // Settings
    this.autoplay = this.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed) * 1000 || 5000;
    this.transitionSpeed = parseInt(this.dataset.transitionSpeed) || 500;
    this.enableSwipe = this.dataset.enableSwipe === 'true';
    
    // State
    this.autoplayInterval = null;
    this.isTransitioning = false;
    
    // Touch/Swipe
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.isDragging = false;
    
    this.init();
  }

  init() {
    if (this.totalSlides <= 1) return;
    
    // Navigation
    if (this.navButtons.prev && this.navButtons.next) {
      this.navButtons.prev.addEventListener('click', () => this.previousSlide());
      this.navButtons.next.addEventListener('click', () => this.nextSlide());
    }
    
    // Pagination
    this.paginationItems.forEach((item, index) => {
      item.addEventListener('click', () => this.goToSlide(index));
    });
    
    // Swipe
    if (this.enableSwipe) {
      this.setupSwipe();
    }
    
    // Autoplay
    if (this.autoplay) {
      this.startAutoplay();
      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());
    }
    
    // Keyboard
    this.setupKeyboard();
    
    // Pause when tab hidden
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.stopAutoplay();
      } else if (this.autoplay) {
        this.startAutoplay();
      }
    });
  }

  setupSwipe() {
    const viewport = this.querySelector('.adv-slideshow__viewport');
    if (!viewport) return;
    
    viewport.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
    viewport.addEventListener('touchmove', (e) => this.handleTouchMove(e), { passive: true });
    viewport.addEventListener('touchend', () => this.handleTouchEnd());
    
    viewport.addEventListener('mousedown', (e) => this.handleTouchStart(e));
    viewport.addEventListener('mousemove', (e) => this.handleTouchMove(e));
    viewport.addEventListener('mouseup', () => this.handleTouchEnd());
    viewport.addEventListener('mouseleave', () => this.handleTouchEnd());
    
    viewport.addEventListener('dragstart', (e) => e.preventDefault());
  }

  handleTouchStart(e) {
    this.isDragging = true;
    this.touchStartX = this.getPositionX(e);
    if (this.autoplay) this.stopAutoplay();
  }

  handleTouchMove(e) {
    if (!this.isDragging) return;
    this.touchEndX = this.getPositionX(e);
  }

  handleTouchEnd() {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    const diff = this.touchStartX - this.touchEndX;
    
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        this.nextSlide();
      } else {
        this.previousSlide();
      }
    }
    
    if (this.autoplay) this.startAutoplay();
  }

  getPositionX(e) {
    return e.type.includes('mouse') ? e.pageX : e.touches[0].clientX;
  }

  setupKeyboard() {
    let isComponentFocused = false;
    
    this.addEventListener('mouseenter', () => isComponentFocused = true);
    this.addEventListener('mouseleave', () => isComponentFocused = false);
    
    document.addEventListener('keydown', (e) => {
      if (!isComponentFocused) return;
      
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.previousSlide();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.nextSlide();
      }
    });
  }

  goToSlide(index) {
    if (this.isTransitioning || index === this.currentSlide) return;
    
    if (index < 0) index = this.totalSlides - 1;
    if (index >= this.totalSlides) index = 0;
    
    this.isTransitioning = true;
    
    // Update slides
    this.slides[this.currentSlide].classList.remove('adv-slideshow__slide--active');
    this.slides[index].classList.add('adv-slideshow__slide--active');
    
    // Update pagination
    if (this.paginationItems.length > 0) {
      this.paginationItems[this.currentSlide].classList.remove('adv-slideshow__pagination-item--active');
      this.paginationItems[index].classList.add('adv-slideshow__pagination-item--active');
    }
    
    this.currentSlide = index;
    
    setTimeout(() => {
      this.isTransitioning = false;
    }, this.transitionSpeed);
    
    if (this.autoplay) {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  nextSlide() {
    this.goToSlide(this.currentSlide + 1);
  }

  previousSlide() {
    this.goToSlide(this.currentSlide - 1);
  }

  startAutoplay() {
    if (!this.autoplay || this.totalSlides <= 1) return;
    
    this.stopAutoplay();
    this.autoplayInterval = setInterval(() => {
      this.nextSlide();
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  disconnectedCallback() {
    this.stopAutoplay();
  }
}

customElements.define('adv-slideshow-component', AdvSlideshowComponent);